import { css } from '@emotion/react';

import { mqMax } from '../breakpoints';
import type { LucidTheme } from '../../types/lucid-theme';

const htmlParserStyles = (theme: LucidTheme) =>
  css([
    {
      color: theme.colors.coolGrey[100],
      fontSize: theme.fontSizes[4],
      lineHeight: theme.lineHeights.paragraph,
      p: {
        fontSize: theme.fontSizes[4],
        lineHeight: theme.lineHeights.paragraph,
      },
      a: {
        color: theme.colors.blue[70],
        fontWeight: theme.fontWeights.strong,
        '&:focus': {
          outline: 'thin solid',
          outlineOffset: '2px',
        },
        '&:hover': {
          fontWeight: theme.fontWeights.regular,
        },
      },
      img: {
        marginBottom: theme.space[4],
      },
      div: {
        fontSize: theme.fontSizes[4],
        lineHeight: theme.lineHeights.paragraph,
      },
      ol: {
        fontSize: theme.fontSizes[4],
        lineHeight: theme.lineHeights.paragraph,
        margin: `0 0 0 ${theme.space.s16}px`,
      },
      li: {
        fontSize: theme.fontSizes[4],
        lineHeight: theme.lineHeights.paragraph,
        margin: `0 0 ${theme.space.s16}px`,
      },
      ul: {
        fontSize: theme.fontSizes[4],
        lineHeight: theme.lineHeights.paragraph,
        margin: `0 0 0 ${theme.space.s16}px`,
      },
      h1: {
        color: theme.colors.heading,
        fontSize: theme.fontSizes[7],
        fontWeight: theme.fontWeights.stronger,
        lineHeight: theme.lineHeights.default,
        marginBottom: theme.space[6],
        marginTop: theme.space[6],
      },
      h2: {
        color: theme.colors.heading,
        fontSize: theme.fontSizes[6],
        fontWeight: theme.fontWeights.stronger,
        lineHeight: theme.lineHeights.default,
        marginBottom: theme.space[5],
        marginTop: theme.space[5],
      },
      h3: {
        color: theme.colors.heading,
        fontSize: theme.fontSizes[4],
        fontWeight: theme.fontWeights.stronger,
        lineHeight: theme.lineHeights.default,
        marginBottom: theme.space[4],
        marginTop: theme.space[4],
      },
      h4: {
        color: theme.colors.heading,
        fontSize: theme.fontSizes[3],
        fontWeight: theme.fontWeights.stronger,
        lineHeight: theme.lineHeights.default,
        marginBottom: theme.space[3],
        marginTop: theme.space[3],
      },
      h5: {
        color: theme.colors.heading,
        fontSize: theme.fontSizes[3],
        fontWeight: theme.fontWeights.stronger,
        lineHeight: theme.lineHeights.default,
        marginBottom: theme.space[3],
        marginTop: theme.space[3],
      },
      h6: {
        color: theme.colors.heading,
        fontSize: theme.fontSizes[3],
        fontWeight: theme.fontWeights.stronger,
        lineHeight: theme.lineHeights.default,
        marginBottom: theme.space[3],
        marginTop: theme.space[3],
      },
      blockquote: {
        fontSize: theme.fontSizes[4],
        lineHeight: theme.lineHeights.body,
        margin: theme.space[6],
        padding: '0 0 0 20px',
        p: {
          color: theme.colors.coolGrey[60],
          paddingLeft: theme.space.s16,
        },
        borderLeft: `solid ${theme.colors.coolGrey[50]} 4px`,
      },
      'b, strong': {
        fontWeight: theme.fontWeights.strong,
      },
      // These classes and styles are needed for some legacy code on the
      //   discovery pages and may not be needed in the future and may
      //   be removed when those are not needed anymore
      '.table-div': {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
      },
      table: {
        width: ' 100%',
        margin: `${theme.space[6]}px ${theme.space[0]}`,
        fontSize: '1.1875rem',
        '&.alternating': {
          th: {
            border: 0,
            paddingBottom: theme.space[0],
          },
          tr: {
            '&:nth-of-type(even)': {
              background: theme.colors.coolGrey[5],
            },
            td: {
              paddingBottom: theme.space[0],
              borderRight: `2px solid ${theme.colors.white}`,
              '&:nth-of-type(2)': {
                textAlign: 'right',
              },
            },
          },
        },
        '&.small-images': {
          td: {
            img: {
              maxHeight: '80px',
            },
          },
        },
        p: {
          margin: theme.space[0],
        },
        '.table-div': {
          '> div': {
            width: '100%',
            display: 'inline-block',
          },
          img: {
            width: '100%',
          },
        },
        '.gatsby-image-wrapper': {
          margin: theme.space[0],
        },
        tr: {
          '&:first-of-type td': {
            paddingTop: theme.space[3],
          },
        },
        td: {
          padding: `${theme.space[1]}px ${theme.space[4]}px ${theme.space[4]}px ${theme.space[0]}`,
          verticalAlign: 'middle',
          minWidth: '10rem',
          [mqMax.mobile]: {
            minWidth: 0,
          },
        },
        th: {
          textAlign: 'left',
          fontWeight: theme.fontWeights.strong,
          borderBottom: `1px solid ${theme.colors.coolGrey[90]}`,
          paddingBottom: theme.space[3],
        },
        '&.symbol-descriptions': {
          tr: {
            verticalAlign: 'top',
          },
          td: {
            padding: theme.space[0],
            verticalAlign: 'middle',
          },
          '&.small': {
            'td:first-of-type': {
              width: '50px',
            },
          },
          '&.large': {
            'td:first-of-type': {
              width: '80px',
            },
          },
          '&.xlarge td:first-of-type': {
            width: '100px',
          },
          'td:first-of-type': {
            width: '70px',
            paddingRight: theme.space[3],
          },
        },
      },
      code: {
        padding: `${theme.space[3]}px ${theme.space[2]}px`,
        background: theme.colors.coolGrey[5],
        color: theme.colors.text,
        border: `1px solid ${theme.colors.coolGrey[30]}`,
        display: 'block',
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '1.2rem',
        whiteSpace: 'pre',
      },
    },
  ]);

export default htmlParserStyles;
